<template>
  <div class="mkp-root">
    <div ref="progressBar" class="mkp-progress"></div>
    <!-- HERO -->
    <section class="mkp-hero" id="inicio">
      <div class="mkp-wrap" data-rv>
        <span class="mkp-eyebrow">Para el wellness médico y la salud integrativa</span>
        <h1>Un centro moderno necesita mucho más que un <em>programa</em>.</h1>
        <p class="mkp-sub">
          Una plataforma que conecte todo lo que ocurre
          <b>antes, durante y después</b> de cada consulta. Capta pacientes,
          cuídalos y haz crecer tu consulta desde un solo lugar.
        </p>
        <div class="mkp-actions">
          <a class="mkp-cta lg wa" :href="waLink">
            Solicitar una demo
            <svg viewBox="0 0 24 24"><path d="M5 12h14m-6-6 6 6-6 6" /></svg>
          </a>
          <a class="mkp-ghost" href="#plataforma">Ver la plataforma</a>
        </div>
      </div>
    </section>

    <!-- PLATAFORMA -->
    <section class="mkp-sec" id="plataforma">
      <div class="mkp-wrap" data-rv>
        <p class="mkp-q">
          ¿Qué ocurre cuando tienes varias herramientas para gestionar tu día a día?
        </p>
        <p class="mkp-a">La información se rompe en islas que no se hablan.</p>
        <p class="mkp-lead">
          La agenda no sabe qué se facturó; la historia clínica, qué se firmó;
          el marketing, quién volvió. Cada herramienta guarda su propia verdad,
          y el equipo se pasa el día haciendo de puente entre todas.
        </p>
        <p class="mkp-bridge">Medkus las reúne todas en una sola plataforma.</p>
        <div class="mkp-tgrid">
          <div v-for="c in platformChips" :key="c.t" class="mkp-tchip">
            <b>{{ c.t }}</b><span>{{ c.s }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- SECCIONES CON TARJETAS -->
    <section
      v-for="s in featureSections"
      :key="s.id"
      class="mkp-sec"
      :id="s.id"
    >
      <div class="mkp-wrap" data-rv>
        <p class="mkp-q">{{ s.q }}</p>
        <p class="mkp-a">{{ s.a }}</p>
        <div class="mkp-fgrid" :class="{ g3: s.cards.length === 3 }">
          <div v-for="c in s.cards" :key="c.h" class="mkp-fcard">
            <h3>{{ c.h }}</h3>
            <p class="d">{{ c.d }}</p>
            <div class="mkp-pills">
              <span v-for="p in c.pills" :key="p">{{ p }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FORMACIÓN -->
    <section class="mkp-sec">
      <div class="mkp-wrap" data-rv>
        <p class="mkp-q">¿Y si tu conocimiento fuera otra fuente de ingresos?</p>
        <p class="mkp-a">Crea y vende tus propios másters, cursos y certificaciones.</p>
        <p class="mkp-lead">
          Empaqueta lo que sabes en formaciones que el público compra online,
          con su temario, su progreso y su certificado. Una nueva línea de
          negocio, dentro de la propia plataforma.
        </p>
        <div class="mkp-pills mkp-pills-loose">
          <span v-for="p in formacionPills" :key="p">{{ p }}</span>
        </div>
      </div>
    </section>

    <!-- DIFERENCIA -->
    <section class="mkp-sec">
      <div class="mkp-wrap" data-rv>
        <p class="mkp-q">¿Por qué Medkus y no una plataforma médica cualquiera?</p>
        <p class="mkp-a">
          Porque está hecho para los centros que las demás no entienden.
        </p>
        <p class="mkp-lead">
          Los directorios y plataformas médicas genéricas nacieron para la
          consulta tradicional. Medkus nace para el centro moderno.
        </p>
        <div class="mkp-contrast">
          <div class="c">
            <h3>Las plataformas genéricas</h3>
            <p>
              Pensadas para pasar consulta. No entienden tus protocolos de
              recovery, tus salas y aforos, tus packs ni tus programas de
              longevidad: los fuerzas hasta que encajan.
            </p>
          </div>
          <div class="c mk">
            <h3>Medkus</h3>
            <p>
              Hecho para tu modelo de trabajo, no al revés. Protocolos,
              recursos, membresías y programas son piezas nativas de la
              plataforma. Habla tu idioma desde el primer día.
            </p>
          </div>
        </div>
        <div class="mkp-tags">
          <span v-for="tag in nicheTags" :key="tag">{{ tag }}</span>
        </div>
      </div>
    </section>

    <!-- PRECIO -->
    <section class="mkp-sec" id="precio">
      <div class="mkp-wrap" data-rv>
        <p class="mkp-q">¿Y cuánto cuesta tenerlo todo conectado?</p>
        <p class="mkp-a">Precios cerrados, sin permanencia. Y el cambio lo hacemos nosotros.</p>
        <div class="mkp-tiers">
          <div v-for="t in tiers" :key="t.lab" class="mkp-tier">
            <span class="lab">{{ t.lab }}</span>
            <div class="price">
              {{ t.price }}<small v-if="t.unit"> {{ t.unit }}</small>
            </div>
            <p class="who">{{ t.who }}</p>
          </div>
        </div>
        <p class="mkp-note">
          Cuentan solo los profesionales con agenda; recepción y administración
          no suman. La IA incluida es un crédito mensual: si un mes se supera,
          el exceso se factura a la tarifa publicada, sin sorpresas.
        </p>
        <div class="mkp-banner">
          <span class="ic">
            <svg viewBox="0 0 24 24">
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7" />
              <path d="M12 3v12M8 11l4 4 4-4" />
            </svg>
          </span>
          <div>
            <b>Migración completa incluida.</b>
            <p>
              Traemos tus pacientes, tu agenda y tu historial desde tu sistema
              actual. Lo hacemos nosotros, sin que pares de trabajar ni un solo
              día.
            </p>
          </div>
        </div>
        <div class="mkp-banner b">
          <span class="ic">
            <svg viewBox="0 0 24 24">
              <path d="M4 13v-2a8 8 0 0 1 16 0v2" />
              <path
                d="M4 13a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2zM20 13a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2z"
              />
              <path d="M18 17v1a3 3 0 0 1-3 3h-2" />
            </svg>
          </span>
          <div>
            <b>Soporte humano, no máquinas.</b>
            <p>
              Cuando necesites ayuda, te responde alguien que entiende tu
              consulta integrativa. Sin bots y sin tickets genéricos.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ALMA / CIERRE -->
    <section class="mkp-sec" id="contacto">
      <div class="mkp-wrap mkp-alma" data-rv>
        <p class="setup">
          Un centro de wellness médico no se gestiona con una agenda bonita.
        </p>
        <p class="punch">
          Medkus es la plataforma del
          <span class="mkp-hl-g">wellness médico</span>.
        </p>
        <p class="mkp-lead">
          Recovery, longevidad, medicina preventiva y rendimiento. Motor de
          disponibilidad propio, historia clínica cifrada e IA integrada, en
          una sola plataforma.
        </p>
        <p class="mission">
          Tu clínica opera en otro nivel.
          <span class="mkp-hl-g">Tu software también debería</span>.
        </p>
        <div class="mkp-actions">
          <a class="mkp-cta lg wa" :href="waLink">
            Solicitar una presentación
            <svg viewBox="0 0 24 24"><path d="M5 12h14m-6-6 6 6-6 6" /></svg>
          </a>
        </div>
        <p class="compliance">
          <b>Facturación conforme a Verifactu.</b> Cumplimiento del RGPD y la
          LOPDGDD en el tratamiento de datos de pacientes.
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSeo, jsonLd } from "@/composables/useSeo";
import forProfessionals from "@/content/forProfessionals";

const { t } = useI18n();
useSeo({
  title: t("seoForProTitle"),
  description: t("seoForProDesc"),
  jsonLd: jsonLd.softwareApp(),
});

const waLink =
  "https://wa.me/34685620353?text=Hola%2C%20quer%C3%ADa%20agendar%20una%20presentaci%C3%B3n%20de%20Medkus";

const { platformChips, featureSections, formacionPills, nicheTags, tiers } =
  forProfessionals;

let observer = null;
const progressBar = ref(null);
let ticking = false;

const updateProgress = () => {
  ticking = false;
  const el = progressBar.value;
  if (!el) return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const p = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
  el.style.transform = `scaleX(${p})`;
};

const onScroll = () => {
  if (!ticking) {
    ticking = true;
    requestAnimationFrame(updateProgress);
  }
};

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
  updateProgress();

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          observer.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12 },
  );
  document.querySelectorAll("[data-rv]").forEach((el) => observer.observe(el));
});
onBeforeUnmount(() => {
  observer && observer.disconnect();
  window.removeEventListener("scroll", onScroll);
});
</script>

<style scoped>
.mkp-root {
  --ink: #0d1f33;
  --text: #46586b;
  --muted: #8a9bad;
  --line: rgb(13 31 51 / 0.1);
  --blue: #2f6dc0;
  --green: #1f9e78;
  --glass-2: rgb(255 255 255 / 0.46);
  --stroke: rgb(255 255 255 / 0.85);
  --hi: rgb(255 255 255 / 0.92);
  --blur: blur(20px) saturate(150%);
  --shadow: 0 30px 70px -40px rgb(13 31 51 / 0.42);
  --shadow-sm: 0 18px 44px -32px rgb(13 31 51 / 0.42);
  --display: "Schibsted Grotesk", system-ui, sans-serif;
  --body: "Hanken Grotesk", system-ui, sans-serif;
  --ease: cubic-bezier(0.2, 0.8, 0.2, 1);

  position: relative;
  font-family: var(--body) !important;
  color: var(--text) !important;
  font-size: 16px !important;
  line-height: 1.6 !important;
  -webkit-font-smoothing: antialiased;
  background:
    radial-gradient(48% 38% at 8% 4%, rgb(47 109 192 / 0.16), transparent 60%),
    radial-gradient(42% 36% at 96% 8%, rgb(31 158 120 / 0.12), transparent 62%),
    radial-gradient(55% 45% at 92% 100%, rgb(47 109 192 / 0.12), transparent 60%),
    linear-gradient(165deg, #eef2f7, #eaf1ee);
}
.mkp-root *,
.mkp-root *::before,
.mkp-root *::after {
  box-sizing: border-box;
}
.mkp-root h1,
.mkp-root h2,
.mkp-root h3,
.mkp-root p {
  margin: 0 !important;
}
.mkp-root a {
  color: inherit;
  text-decoration: none;
}

.mkp-wrap {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 clamp(18px, 5vw, 32px);
}
.mkp-sec {
  padding: clamp(54px, 9vw, 104px) 0;
}
.mkp-eyebrow {
  font-family: var(--display);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--blue);
}

/* question / answer */
.mkp-q {
  font-family: var(--display);
  font-weight: 700;
  color: var(--blue) !important;
  font-size: clamp(26px, 5vw, 38px) !important;
  line-height: 1.12 !important;
  letter-spacing: -0.02em;
  max-width: 18ch;
}
.mkp-a {
  font-family: var(--display);
  font-weight: 600;
  color: var(--ink) !important;
  font-size: clamp(18px, 2.6vw, 22px) !important;
  line-height: 1.3 !important;
  margin-top: 12px !important;
  max-width: 30ch;
}
.mkp-lead {
  font-size: clamp(15px, 2vw, 17px) !important;
  color: var(--text) !important;
  margin-top: 16px !important;
  max-width: 60ch;
  line-height: 1.6 !important;
}
.mkp-lead b {
  color: var(--ink);
  font-weight: 600;
}
.mkp-hl-g {
  color: var(--green);
}

/* hero */
.mkp-hero {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(48px, 8vw, 80px) 0 clamp(40px, 7vw, 64px);
}
.mkp-hero h1 {
  font-family: var(--display);
  font-weight: 800;
  color: var(--ink) !important;
  font-size: clamp(34px, 7vw, 62px) !important;
  line-height: 1.02 !important;
  letter-spacing: -0.035em;
  margin-top: 18px !important;
  max-width: 16ch;
}
.mkp-hero h1 em {
  font-style: normal;
  color: var(--blue);
}
.mkp-hero .mkp-sub {
  font-size: clamp(16px, 2.4vw, 20px) !important;
  color: var(--text) !important;
  max-width: 46ch;
  margin-top: 20px !important;
  font-weight: 500;
  line-height: 1.45 !important;
}
.mkp-hero .mkp-sub b {
  color: var(--ink);
  font-weight: 600;
}
.mkp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 30px;
  align-items: center;
}

/* buttons */
.mkp-cta {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 11px 20px;
  border-radius: 999px;
  background: var(--blue);
  color: #fff !important;
  font-family: var(--display);
  font-weight: 600;
  font-size: 14.5px;
  box-shadow: 0 14px 30px -14px rgb(47 109 192 / 0.9);
  transition: transform 0.15s var(--ease);
}
.mkp-cta:hover {
  transform: translateY(-1px);
}
.mkp-cta:active {
  transform: translateY(0);
}
.mkp-cta svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.mkp-cta.lg {
  padding: 15px 28px;
  font-size: 16px;
}
.mkp-cta.wa {
  background: #1f9e78;
  box-shadow: 0 14px 30px -14px rgb(31 158 120 / 0.9);
}
.mkp-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  border-radius: 999px;
  border: 1px solid var(--stroke);
  background: rgb(255 255 255 / 0.5);
  color: var(--ink) !important;
  font-family: var(--display);
  font-weight: 600;
  font-size: 15px;
}

/* plataforma */
.mkp-bridge {
  font-family: var(--display);
  font-weight: 600;
  font-size: clamp(16px, 2.2vw, 19px) !important;
  color: var(--ink) !important;
  margin-top: 18px !important;
}
.mkp-tgrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 26px;
}
.mkp-tchip {
  border-radius: 14px;
  padding: 15px 17px;
  background: var(--glass-2);
  backdrop-filter: var(--blur);
  -webkit-backdrop-filter: var(--blur);
  border: 1px solid var(--stroke);
  box-shadow: inset 0 1px 0 var(--hi), var(--shadow-sm);
}
.mkp-tchip b {
  font-family: var(--display);
  font-weight: 600;
  font-size: 15px;
  color: var(--ink);
  display: block;
}
.mkp-tchip span {
  font-size: 12.5px;
  color: var(--muted);
}

/* feature cards */
.mkp-fgrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 28px;
}
.mkp-fcard {
  border-radius: 20px;
  background: var(--glass-2);
  backdrop-filter: var(--blur);
  -webkit-backdrop-filter: var(--blur);
  border: 1px solid var(--stroke);
  box-shadow: inset 0 1px 0 var(--hi), var(--shadow-sm);
  padding: 24px;
}
.mkp-fcard h3 {
  font-family: var(--display);
  font-weight: 600;
  font-size: 18px !important;
  color: var(--ink) !important;
  margin-bottom: 5px !important;
}
.mkp-fcard .d {
  font-size: 14px !important;
  color: var(--muted) !important;
  margin-bottom: 15px !important;
  line-height: 1.45 !important;
}
.mkp-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.mkp-pills span {
  font-family: var(--display);
  font-weight: 600;
  font-size: 12.5px;
  color: var(--blue);
  background: rgb(47 109 192 / 0.07);
  border: 1px solid rgb(47 109 192 / 0.16);
  border-radius: 999px;
  padding: 6px 13px;
}
.mkp-pills-loose {
  margin-top: 18px;
}

/* diferencia */
.mkp-contrast {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-top: 28px;
}
.mkp-contrast .c {
  border-radius: 20px;
  padding: 24px;
  border: 1px solid var(--stroke);
  box-shadow: inset 0 1px 0 var(--hi), var(--shadow-sm);
  background: var(--glass-2);
}
.mkp-contrast .c.mk {
  background: linear-gradient(
    160deg,
    rgb(47 109 192 / 0.1),
    rgb(31 158 120 / 0.07)
  );
}
.mkp-contrast h3 {
  font-family: var(--display);
  font-weight: 600;
  font-size: 19px !important;
  color: var(--ink) !important;
  margin-bottom: 8px !important;
}
.mkp-contrast p {
  font-size: 14.5px !important;
  color: var(--text) !important;
  line-height: 1.55 !important;
}
.mkp-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
}
.mkp-tags span {
  font-family: var(--display);
  font-weight: 600;
  font-size: 13px;
  color: var(--blue);
  background: rgb(47 109 192 / 0.08);
  border: 1px solid rgb(47 109 192 / 0.18);
  border-radius: 999px;
  padding: 8px 15px;
}

/* precio */
.mkp-tiers {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 28px;
}
.mkp-tier {
  border-radius: 20px;
  padding: 24px;
  background: var(--glass-2);
  backdrop-filter: var(--blur);
  -webkit-backdrop-filter: var(--blur);
  border: 1px solid var(--stroke);
  box-shadow: inset 0 1px 0 var(--hi), var(--shadow-sm);
}
.mkp-tier .lab {
  font-family: var(--display);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--blue);
}
.mkp-tier .price {
  font-family: var(--display);
  font-weight: 800;
  font-size: 26px;
  color: var(--ink) !important;
  margin: 12px 0 4px !important;
  letter-spacing: -0.02em;
}
.mkp-tier .price small {
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
}
.mkp-tier .who {
  font-size: 14px !important;
  color: var(--text) !important;
}
.mkp-banner {
  margin-top: 16px;
  border-radius: 18px;
  padding: 20px 22px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: linear-gradient(
    160deg,
    rgb(31 158 120 / 0.12),
    rgb(47 109 192 / 0.07)
  );
  border: 1px solid #d7eee6;
  box-shadow: inset 0 1px 0 var(--hi);
}
.mkp-banner.b {
  background: linear-gradient(
    160deg,
    rgb(47 109 192 / 0.1),
    rgb(47 109 192 / 0.05)
  );
  border-color: #d3e3f3;
}
.mkp-banner .ic {
  width: 46px;
  height: 46px;
  flex: none;
  border-radius: 13px;
  display: grid;
  place-items: center;
  color: var(--green);
  background: rgb(31 158 120 / 0.14);
  border: 1px solid rgb(31 158 120 / 0.26);
}
.mkp-banner.b .ic {
  color: var(--blue);
  background: rgb(47 109 192 / 0.12);
  border-color: rgb(47 109 192 / 0.24);
}
.mkp-banner .ic svg {
  width: 23px;
  height: 23px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.mkp-banner b {
  display: block;
  font-family: var(--display);
  font-weight: 600;
  font-size: 16px;
  color: var(--ink);
  margin-bottom: 3px !important;
}
.mkp-banner p {
  font-size: 14px !important;
  color: var(--text) !important;
  line-height: 1.5 !important;
}
.mkp-note {
  margin-top: 18px !important;
  font-size: 12.5px !important;
  color: var(--muted) !important;
  line-height: 1.55 !important;
  max-width: 64ch;
}

/* alma / cierre */
.mkp-alma {
  text-align: left;
}
.mkp-alma .setup {
  font-size: clamp(17px, 2.4vw, 21px) !important;
  color: var(--text) !important;
  max-width: 32ch;
}
.mkp-alma .punch {
  font-family: var(--display);
  font-weight: 800;
  color: var(--ink) !important;
  font-size: clamp(30px, 6vw, 52px) !important;
  line-height: 1.05 !important;
  letter-spacing: -0.03em;
  margin-top: 14px !important;
  max-width: 16ch;
}
.mkp-alma .mission {
  font-family: var(--display);
  font-weight: 700;
  color: var(--ink) !important;
  font-size: clamp(20px, 3vw, 26px) !important;
  margin-top: 24px !important;
  letter-spacing: -0.02em;
}
.mkp-alma .compliance {
  margin-top: 24px !important;
  font-size: 12.5px !important;
  color: var(--muted) !important;
  line-height: 1.55 !important;
  max-width: 64ch;
}
.mkp-alma .compliance b {
  color: var(--text);
  font-weight: 600;
}

/* scroll progress */
.mkp-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 10000;
  background: linear-gradient(90deg, var(--blue), var(--green));
  transform: scaleX(0);
  transform-origin: 0 50%;
  pointer-events: none;
}

/* reveal */
@media (prefers-reduced-motion: no-preference) {
  .mkp-root [data-rv] {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.7s var(--ease), transform 0.7s var(--ease);
  }
  .mkp-root [data-rv].in {
    opacity: 1;
    transform: none;
  }

  /* headline + card cascade inside each revealed section */
  .mkp-root [data-rv] .mkp-eyebrow,
  .mkp-root [data-rv] h1,
  .mkp-root [data-rv] .mkp-sub,
  .mkp-root [data-rv] .mkp-actions,
  .mkp-root [data-rv] .mkp-q,
  .mkp-root [data-rv] .mkp-a,
  .mkp-root [data-rv] .mkp-lead,
  .mkp-root [data-rv] .mkp-tchip,
  .mkp-root [data-rv] .mkp-fcard,
  .mkp-root [data-rv] .mkp-tier,
  .mkp-root [data-rv] .mkp-contrast .c,
  .mkp-root [data-rv] .mkp-banner {
    opacity: 0;
    transform: translateY(26px) scale(0.98);
    transition:
      opacity 0.6s var(--ease),
      transform 0.6s var(--ease);
  }
  .mkp-root [data-rv].in .mkp-eyebrow,
  .mkp-root [data-rv].in h1,
  .mkp-root [data-rv].in .mkp-sub,
  .mkp-root [data-rv].in .mkp-actions,
  .mkp-root [data-rv].in .mkp-q,
  .mkp-root [data-rv].in .mkp-a,
  .mkp-root [data-rv].in .mkp-lead,
  .mkp-root [data-rv].in .mkp-tchip,
  .mkp-root [data-rv].in .mkp-fcard,
  .mkp-root [data-rv].in .mkp-tier,
  .mkp-root [data-rv].in .mkp-contrast .c,
  .mkp-root [data-rv].in .mkp-banner {
    opacity: 1;
    transform: none;
  }

  .mkp-root [data-rv].in h1,
  .mkp-root [data-rv].in .mkp-a {
    transition-delay: 0.08s;
  }
  .mkp-root [data-rv].in .mkp-sub,
  .mkp-root [data-rv].in .mkp-lead {
    transition-delay: 0.16s;
  }
  .mkp-root [data-rv].in .mkp-actions {
    transition-delay: 0.24s;
  }
  .mkp-root [data-rv].in .mkp-banner {
    transition-delay: 0.3s;
  }

  .mkp-root [data-rv].in .mkp-tchip:nth-child(2),
  .mkp-root [data-rv].in .mkp-fcard:nth-child(2),
  .mkp-root [data-rv].in .mkp-tier:nth-child(2),
  .mkp-root [data-rv].in .mkp-contrast .c:nth-child(2) {
    transition-delay: 0.07s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(3),
  .mkp-root [data-rv].in .mkp-fcard:nth-child(3),
  .mkp-root [data-rv].in .mkp-tier:nth-child(3) {
    transition-delay: 0.14s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(4) {
    transition-delay: 0.21s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(5) {
    transition-delay: 0.28s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(6) {
    transition-delay: 0.35s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(7) {
    transition-delay: 0.42s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(8) {
    transition-delay: 0.49s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(9) {
    transition-delay: 0.56s;
  }
  .mkp-root [data-rv].in .mkp-tchip:nth-child(n + 10) {
    transition-delay: 0.63s;
  }
}

/* tablet */
@media (min-width: 680px) {
  .mkp-tgrid {
    grid-template-columns: 1fr 1fr;
  }
  .mkp-fgrid {
    grid-template-columns: 1fr 1fr;
  }
  .mkp-contrast {
    grid-template-columns: 1fr 1fr;
  }
  .mkp-tiers {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
/* desktop */
@media (min-width: 1024px) {
  .mkp-tgrid {
    grid-template-columns: 1fr 1fr 1fr;
  }
  .mkp-fgrid.g3 {
    grid-template-columns: 1fr 1fr 1fr;
  }
  .mkp-hero {
    min-height: 84vh;
  }
}
</style>
